// @flow
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  DatePicker,
  Input,
  Modal,
} from 'antd';
import { SpH5, SpError, SpButton } from 'components/DesignKit';
import SimpleReactValidator from 'simple-react-validator';
import { vendorsActions } from 'store/actions';
import formatNumber from 'utils/formatNumber';
import moment from 'moment-timezone';

type Props = {
  visible: boolean,
  close: Function,
  submit: Function,
  submitting: Function,
  selectedInvoice: Object,
  selectedAccount: Object,
};

const dateFormat = 'MMM DD, YYYY';

const MOMENT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const EditPartialPayment = (props: Props) => {
  const {
    visible,
    close,
    submit,
    submitting,
    selectedInvoice,
    selectedAccount,
  } = props;
  const { invoiceTermsAndConditions: invoiceTermsAndConditionsDetail, coupon, paymentDueDate } = selectedInvoice;
  const [, forceUpdate] = useState();
  const prefix = selectedInvoice?.currency?.prefix;
  const currencyObj = selectedAccount && selectedAccount.currency;
  const totalAmount = selectedInvoice?.total && selectedInvoice?.total / 100;
  const [initialAmount, setInitialAmount] = useState(invoiceTermsAndConditionsDetail?.initialAmount
    ? (invoiceTermsAndConditionsDetail?.initialAmount / 100).toFixed(2) : '');
  const [lateFee, setLateFee] = useState(invoiceTermsAndConditionsDetail?.lateFee ? (invoiceTermsAndConditionsDetail?.lateFee / 100).toFixed(2) : '');
  const [overdueInterest, setOverdueInterest] = useState(invoiceTermsAndConditionsDetail?.overdueInterest
    ? (invoiceTermsAndConditionsDetail?.overdueInterest).toFixed(2) : null);
  const [graceDays, setGraceDays] = useState(invoiceTermsAndConditionsDetail?.graceDays ? invoiceTermsAndConditionsDetail?.graceDays : null);
  const dateExpiry = moment.utc(selectedInvoice.expiryDate).tz(selectedAccount.timezone).format(dateFormat);
  const [expiryDate, setExpiryDate] = useState(selectedInvoice?.expiryDate ? selectedInvoice?.expiryDate : null);
  const gid = selectedInvoice && selectedInvoice.gid;
  const [disableButton, setDisableButton] = useState(false);

  const simpleValidator = useRef(new SimpleReactValidator({
    validators: {
      gracePeriodRange: {
        message: 'Enter a number between 0 - 90',
        rule: (val) => {
          const num = Number(val);
          return !Number.isNaN(num) && num >= 0 && num <= 90;
        },
      },
      minTenPercent: {
        message: 'Initial payment must be at least 10% of the total amount.',
        rule: (val, params) => {
          const numericVal = Number(val && val.replace(/,/g, ''));
          const totalPrice = Number(params[0]);
          return numericVal >= totalPrice * 0.1;
        },
      },
      maxTenPercent: {
        message: 'Late Fee should not be more than 10% of Total Invoice amount',
        rule: (val, params) => {
          const numericVal = Number(val && val.replace(/,/g, ''));
          const totalPrice = Number(params[0]);
          return numericVal <= totalPrice * 0.1;
        },
      },
      expiryDate: {
        message: 'Expiry Date must be greater than Due Date',
        rule: (val, params) => {
          const dueDate = params[0];
          return dueDate < val;
        },
      },
      interestIfOverdue: {
        message: 'Interest rate must not exceed 100%',
        rule: val => Number(val) <= 100,
      },
    },
  }));

  useEffect(() => {
    if (!submitting) {
      setDisableButton(false);
    }
  }, [submitting]);

  const getAmountOff = (value) => {
    let amountOff;
    if (value.percentageOff) {
      const amount = (totalAmount * value.percentageOff) / 100;
      amountOff = formatNumber((amount).toFixed(2), currencyObj);
    } else if (value.amountOff) {
      amountOff = formatNumber((value.amountOff / 100).toFixed(2), currencyObj);
    }
    return amountOff;
  };

  const onExpiryDateChange = (date) => {
    // eslint-disable-next-line
    const Date = moment(date).format(MOMENT_FORMAT);
    const selectedDate = moment.tz(Date, selectedAccount.timezone);
    const utcDate = selectedDate.utc().format(MOMENT_FORMAT);
    setExpiryDate(utcDate);
  };

  const formatToTwoDecimals = num => (Math.floor(num * 100) / 100);
  const pendingAmount = (Number(
    totalAmount - Number(initialAmount && initialAmount?.replace(/,/g, '')),
  )).toFixed(2);

  const validate = (event) => {
    setDisableButton(true);
    event.preventDefault();
    const formValid = simpleValidator.current.allValid();
    setDisableButton(formValid);
    if (!formValid) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    submit({
      gid,
      initialAmount: initialAmount ? initialAmount * 100 : null,
      lateFee: lateFee ? lateFee * 100 : null,
      graceDays,
      overdueInterest,
      expiryDate,
      pendingAmount: pendingAmount ? formatToTwoDecimals(pendingAmount * 100) : null,
    });
  };
  return (
    <Modal
      centered
      closable={false}
      visible={visible}
      footer={[
        <SpButton onClick={() => close()} type="secondary">Cancel</SpButton>,
        <SpButton onClick={validate} disabled={disableButton}>Update</SpButton>,
      ]}
      title="Update Partial Payment"
    >
      <div className="px-4">
        <div className="mb-3">
          <label htmlFor=""><SpH5>Initial Amount</SpH5></label>
          <Input
            name="initialAmount"
            value={initialAmount}
            placeholder="0.00"
            prefix={prefix}
            onChange={(e) => {
              const regex = /^\d*\.?\d*$/;
              const { value } = e.currentTarget;
              if (regex.test(value) || value === '') {
                setInitialAmount(e.currentTarget.value);
              } else if (!regex.test(value)) {
                setInitialAmount(0.00);
              }
            }}
            onBlur={e => {
              if (e.currentTarget.value) {
                const { value } = e.currentTarget;
                const rateValue = parseFloat(value);
                if (Number.isInteger(rateValue)) {
                  setInitialAmount(parseInt(value, 10).toFixed(2));
                }
              }
            }}
            onKeyPress={e => {
              const keyCode = e.charCode || e.which;
              if ((keyCode < 48 || keyCode > 57) && keyCode !== 46 && keyCode !== 44) {
                e.preventDefault();
              }
            }}
          />
          <SpError>
            {simpleValidator.current.message('Initial Payment', initialAmount,
              `required|minTenPercent:${coupon ? getAmountOff(coupon) : totalAmount}`)}
          </SpError>
        </div>
        <div className="mb-3">
          <label htmlFor=""><SpH5>Late Fee</SpH5></label>
          <Input
            name="lateFee"
            value={lateFee}
            placeholder="0.00"
            prefix={prefix}
            onChange={(e) => {
              const regex = /^\d*\.?\d*$/;
              const { value } = e.currentTarget;
              if (regex.test(value) || value === '') {
                setLateFee(e.currentTarget.value);
              } else if (!regex.test(value)) {
                setLateFee(0.00);
              }
            }}
            onBlur={e => {
              if (e.currentTarget.value) {
                const { value } = e.currentTarget;
                const rateValue = parseFloat(value);
                if (Number.isInteger(rateValue)) {
                  setLateFee(parseInt(value, 10).toFixed(2));
                }
              }
            }}
            onKeyPress={e => {
              const keyCode = e.charCode || e.which;
              if ((keyCode < 48 || keyCode > 57) && keyCode !== 46 && keyCode !== 44) {
                e.preventDefault();
              }
            }}
          />
          <SpError>
            {simpleValidator.current.message('Late Fee', lateFee,
              `maxTenPercent:${totalAmount}`)}
          </SpError>
        </div>
        <div className="mb-3">
          <label htmlFor=""><SpH5>Interest if Overdue (optional)</SpH5></label>
          <Input
            name="overdueInterest"
            value={overdueInterest}
            placeholder="Percentage"
            prefix="%"
            onChange={(e) => {
              const regex = /^\d*\.?\d*$/;
              const { value } = e.currentTarget;
              if (regex.test(value) || value === '') {
                setOverdueInterest(e.currentTarget.value);
              } else if (!regex.test(value)) {
                setOverdueInterest(0.00);
              }
            }}
            onBlur={e => {
              if (e.currentTarget.value) {
                const { value } = e.currentTarget;
                const rateValue = parseFloat(value);
                if (Number.isInteger(rateValue)) {
                  setOverdueInterest(parseInt(value, 10).toFixed(2));
                }
              }
            }}
            onKeyPress={e => {
              const keyCode = e.charCode || e.which;
              if ((keyCode < 48 || keyCode > 57) && keyCode !== 46 && keyCode !== 44) {
                e.preventDefault();
              }
            }}
          />
          <SpError>
            {simpleValidator.current.message('Interest if Overdue', overdueInterest, 'interestIfOverdue')}
          </SpError>
        </div>
        <div className="mb-3">
          <label htmlFor=""><SpH5>Grace Period</SpH5></label>
          <Input
            name="graceDays"
            value={graceDays}
            placeholder="0 to 90 Days"
            onChange={(e) => setGraceDays(e.target.value)}
          />
          <SpError>
            {simpleValidator.current.message('grace period', graceDays,
              ((overdueInterest !== '' && overdueInterest !== null) || (lateFee !== '' && lateFee !== null))
                ? 'required|gracePeriodRange' : 'gracePeriodRange')}
          </SpError>
        </div>
        <div>
          <label htmlFor=""><SpH5>Expiry Date</SpH5></label>
          <DatePicker
            defaultValue={moment(dateExpiry, dateFormat)}
            format={dateFormat}
            allowClear={false}
            disabledDate={current => current && current < moment().subtract(1, 'days')}
            onChange={onExpiryDateChange}
          />
          <SpError>
            {simpleValidator.current.message('Expiry Date', expiryDate, `required|expiryDate:${paymentDueDate}`)}
          </SpError>
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  validator: state.loading.validator,
  submitting: state.loading.submitting,
  test: state.account.test,
  customSuggestions: state.vendors.customSuggestions,
});

const mapDispatchToProps = (dispatch) => ({
  fetchCustomTypes: param => dispatch(vendorsActions.fetchCustomTypes({
    payload: param,
  })),
  deleteSingleCustomType: param => dispatch(vendorsActions.deleteSingleCustomType({
    payload: param,
  })),
  deleteAllCustomTypes: param => dispatch(vendorsActions.deleteAllCustomTypes({
    payload: param,
  })),
});
// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(EditPartialPayment);
